// hooks/site/useSite.js
import { useCallback, useMemo } from 'react';
import { useSiteContext } from './context';
import { ACTIONS } from './reducer';
import { apiCaller, APIError, createSearchCriteria, createPageable } from 'utils/axios';
import { API_ENDPOINTS } from 'hooks/api/apiEndpoints';
import Swal from 'sweetalert2';

export const useSite = () => {
    const [state, dispatch] = useSiteContext();

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const handleError = useCallback(
        (error) => {
            let errorMessage = 'An unexpected error occurred';
            let errors = null;

            if (error instanceof APIError) {
                errorMessage = error.message;
                errors = error.errors;
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    const setFilter = useCallback(
        (filterData) => {
            dispatch({ type: ACTIONS.SET_FILTER, payload: filterData });
        },
        [dispatch]
    );

    const setSort = useCallback(
        (sortData) => {
            dispatch({ type: ACTIONS.SET_SORT, payload: sortData });
        },
        [dispatch]
    );

    const setPage = useCallback(
        (pageData) => {
            dispatch({ type: ACTIONS.SET_PAGE, payload: pageData });
        },
        [dispatch]
    );

    const fetchSites = useCallback(async () => {
        try {
            setLoading(true);
            const { filter, pagination, sort } = state;

            const searchCriteria = Object.entries(filter)
                .filter(([_, value]) => value !== null && value !== undefined)
                .map(([key, value]) =>
                    createSearchCriteria({
                        key,
                        value,
                        operation: 'EQUAL'
                    })
                );

            const pageable = createPageable({
                page: pagination.page,
                size: pagination.size,
                sort: [{ field: sort.field, direction: sort.direction }]
            });

            const data = await apiCaller.search(API_ENDPOINTS.SITES, searchCriteria, pageable);
            dispatch({ type: ACTIONS.SET_SITES, payload: data });
            return data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, dispatch, state]);

    const fetchSiteByDistrict = useCallback(
        async (districtId) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.SITES}/district/${districtId}`);
                dispatch({ type: ACTIONS.SET_ALL_SITES, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const searchSites = useCallback(
        async (searchParams = {}, pageParams = {}) => {
            setFilter(searchParams);
            setPage(pageParams);
            return fetchSites();
        },
        [setFilter, setPage, fetchSites]
    );

    const createSite = useCallback(
        async (siteData) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(API_ENDPOINTS.SITES, siteData);
                dispatch({ type: ACTIONS.ADD_SITE, payload: data });

                showSuccessMessage('Site created successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateSite = useCallback(
        async (id, siteData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.SITES}/${id}`, siteData);
                dispatch({ type: ACTIONS.UPDATE_SITE, payload: { id, data } });

                showSuccessMessage('Site updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const deleteSite = useCallback(
        async (id) => {
            try {
                setLoading(true);
                await apiCaller.delete(`${API_ENDPOINTS.SITES}/${id}`);
                dispatch({ type: ACTIONS.DELETE_SITE, payload: id });

                showSuccessMessage('Site deleted successfully');
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const getSiteById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.SITES}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_SITE, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    const listSite = useCallback(
        async (page = 0, size = 10) => {
            try {
                setLoading(true);
                const data = await apiCaller.list(API_ENDPOINTS.SITES, page, size);
                dispatch({ type: ACTIONS.SET_SITES, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    return useMemo(
        () => ({
            // State
            sites: state.sites,
            selectedSite: state.selectedSite,
            loading: state.loading,
            error: state.error,
            filter: state.filter,
            pagination: state.pagination,
            sort: state.sort,
            lastUpdated: state.lastUpdated,
            districtId: state.districtId,

            // List Operations
            setFilter,
            setSort,
            setPage,
            fetchSites,
            searchSites,
            fetchSiteByDistrict,

            // CRUD Operations
            createSite,
            updateSite,
            deleteSite,
            getSiteById,

            // Utilities
            clearError,
            listSite
        }),
        [
            state.sites,
            state.selectedSite,
            state.loading,
            state.error,
            state.filter,
            state.pagination,
            state.sort,
            state.lastUpdated,
            state.districtId,
            setFilter,
            setSort,
            setPage,
            fetchSites,
            searchSites,
            fetchSiteByDistrict,
            createSite,
            updateSite,
            deleteSite,
            getSiteById,
            clearError,
            listSite
        ]
    );
};

export default useSite;
